import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Row, Col } from 'react-bootstrap';
import { useDiagramStore } from '../store/diagramStore';
import { ServiceData, NetworkZone, ServiceGroup, NodeStatus } from '../types/diagram.types';

const GROUPS: ServiceGroup[] = [
  'Gateways', 'Auth/User', 'Finance', 'Trading',
  'Market Data', 'Quick Exchange', 'Acquiring', 'Crypto', 'Compliance', 'Notifications',
];
const ZONES: NetworkZone[] = ['zone-external', 'zone-dmz', 'zone-internal', 'zone-partners'];
const STATUSES: NodeStatus[] = ['active', 'planned', 'deprecated'];

interface EditServiceModalProps {
  show: boolean;
  nodeId: string | null;
  onHide: () => void;
}

const EditServiceModal: React.FC<EditServiceModalProps> = ({ show, nodeId, onHide }) => {
  const { nodes, updateService } = useDiagramStore();
  const node = nodes.find(n => n.id === nodeId);
  const svc = node?.data as ServiceData | undefined;

  const [form, setForm] = useState<Partial<ServiceData>>({});
  const [techInput, setTechInput] = useState('');
  const [dbInput, setDbInput] = useState('');

  useEffect(() => {
    if (svc) {
      setForm({ ...svc });
      setTechInput((svc.techStack ?? []).join(', '));
      setDbInput((svc.databases ?? []).join(', '));
    }
  }, [nodeId, show]);

  const set = (key: keyof ServiceData, val: any) =>
    setForm(f => ({ ...f, [key]: val }));

  const handleSave = () => {
    if (!nodeId || !form.name?.trim()) return;
    updateService(nodeId, form);
    onHide();
  };

  if (!svc) return null;

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Edit: {svc.name}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Row className="mb-3">
          <Col>
            <Form.Label>Name *</Form.Label>
            <Form.Control
              value={form.name ?? ''}
              onChange={e => set('name', e.target.value)}
            />
          </Col>
          <Col>
            <Form.Label>Version</Form.Label>
            <Form.Control
              value={form.version ?? ''}
              onChange={e => set('version', e.target.value)}
            />
          </Col>
        </Row>
        <Row className="mb-3">
          <Col>
            <Form.Label>Group</Form.Label>
            <Form.Select value={form.group} onChange={e => set('group', e.target.value as ServiceGroup)}>
              {GROUPS.map(g => <option key={g}>{g}</option>)}
            </Form.Select>
          </Col>
          <Col>
            <Form.Label>Zone</Form.Label>
            <Form.Select value={form.zone} onChange={e => set('zone', e.target.value as NetworkZone)}>
              {ZONES.map(z => <option key={z} value={z}>{z}</option>)}
            </Form.Select>
          </Col>
          <Col>
            <Form.Label>Status</Form.Label>
            <Form.Select value={form.status} onChange={e => set('status', e.target.value as NodeStatus)}>
              {STATUSES.map(s => <option key={s}>{s}</option>)}
            </Form.Select>
          </Col>
        </Row>
        <Row className="mb-3">
          <Col>
            <Form.Label>Tech Stack</Form.Label>
            <Form.Control
              value={techInput}
              onChange={e => {
                setTechInput(e.target.value);
                set('techStack', e.target.value.split(',').map(s => s.trim()).filter(Boolean));
              }}
            />
          </Col>
          <Col>
            <Form.Label>Databases</Form.Label>
            <Form.Control
              value={dbInput}
              onChange={e => {
                setDbInput(e.target.value);
                set('databases', e.target.value.split(',').map(s => s.trim()).filter(Boolean));
              }}
            />
          </Col>
        </Row>
        <Form.Group className="mb-3">
          <Form.Label>Repository</Form.Label>
          <Form.Control
            value={form.repository ?? ''}
            onChange={e => set('repository', e.target.value)}
          />
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label>Description</Form.Label>
          <Form.Control
            as="textarea"
            rows={2}
            value={form.description ?? ''}
            onChange={e => set('description', e.target.value)}
          />
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>Cancel</Button>
        <Button variant="primary" onClick={handleSave} disabled={!form.name?.trim()}>
          Save Changes
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default EditServiceModal;
